import { Injectable } from '@angular/core';
import { map, Observable, of, tap, shareReplay, catchError, finalize, timeout, throwError } from 'rxjs';
import { LMSAPIClient } from './http-services/http-service';

export interface LmsRecord {
    key: string;
    value: string;
}

export interface LmsResponse {
    data: LmsRecord[];
}

@Injectable({
    providedIn: 'root',
})
export class LmsService {
    private readonly STORAGE_KEY_PREFIX = 'translations_';
    private readonly TRANSLATIONS_FETCHED_KEY = 'translations_fetched';
    private fetchRequest$: Observable<Record<string, Record<string, string>>> | null = null;

    constructor(private lmsClient: LMSAPIClient) { }

    public loadTranslations(language: string): Observable<Map<string, string>> {
        const storageKey = `${this.STORAGE_KEY_PREFIX}${language}`;

        const getCachedTranslation = () => {
            const cached = localStorage.getItem(storageKey);
            return cached ? new Map<string, string>(JSON.parse(cached)) : null;
        };

        const shouldFetch = !sessionStorage.getItem(this.TRANSLATIONS_FETCHED_KEY);

        if (!shouldFetch) {
            const cached = getCachedTranslation();
            if (cached) {
                return of(cached);
            }

            return throwError(() => new Error(`No translations found for '${language}'`));
        }

        if (!this.fetchRequest$) {
            this.fetchRequest$ = this.lmsClient
                .get<Record<string, Record<string, string>>>('/GetWebTranslations')
                .pipe(
                    timeout(3000),
                    map(rawResponse => {
                        const normalizedResponse: Record<string, Record<string, string>> = {};

                        Object.keys(rawResponse).forEach(key => {
                            const shortLang = key.split('-')[0].toLowerCase();
                            normalizedResponse[shortLang] = rawResponse[key];
                        });

                        return normalizedResponse;
                    }),
                    tap(response => {
                        Object.keys(response).forEach(lang => {
                            const langKey = `${this.STORAGE_KEY_PREFIX}${lang}`;
                            const translationMap = new Map<string, string>(Object.entries(response[lang]));

                            localStorage.setItem(
                                langKey,
                                JSON.stringify(Array.from(translationMap.entries()))
                            );
                        });

                        sessionStorage.setItem(this.TRANSLATIONS_FETCHED_KEY, 'true');
                    }),
                    shareReplay(1),
                    finalize(() => {
                        this.fetchRequest$ = null;
                    })
                );
        }

        return this.fetchRequest$.pipe(
            map(response => {
                const newTranslations = response[language];

                if (newTranslations) {
                    return new Map<string, string>(Object.entries(newTranslations));
                }

                const cached = getCachedTranslation();
                if (cached) {
                    return cached;
                }

                throw new Error(`No translations found for '${language}'`);
            }),
            catchError(error => {
                const cached = getCachedTranslation();

                if (cached) {
                    return of(cached);
                }

                return throwError(() => error);
            })
        );
    }
}
